import { useState } from 'react';

type HashAlgorithm = 'MD5' | 'SHA-1' | 'SHA-256' | 'SHA-512';

export default function HashGenerator() {
    const [input, setInput] = useState('');
    const [hashes, setHashes] = useState<Record<HashAlgorithm, string>>({
        'MD5': '',
        'SHA-1': '',
        'SHA-256': '',
        'SHA-512': '',
    });

    const generateHashes = async () => {
        if (!input) {
            setHashes({ 'MD5': '', 'SHA-1': '', 'SHA-256': '', 'SHA-512': '' });
            return;
        }

        const encoder = new TextEncoder();
        const data = encoder.encode(input);

        try {
            const algorithms: { name: HashAlgorithm; algo: string }[] = [
                { name: 'SHA-1', algo: 'SHA-1' },
                { name: 'SHA-256', algo: 'SHA-256' },
                { name: 'SHA-512', algo: 'SHA-512' },
            ];

            const results: Partial<Record<HashAlgorithm, string>> = {};

            for (const { name, algo } of algorithms) {
                const hashBuffer = await crypto.subtle.digest(algo, data);
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
                results[name] = hashHex;
            }

            // Note: MD5 is not available in Web Crypto API
            results['MD5'] = 'MD5 not available in browser (use SHA-256 instead)';

            setHashes(results as Record<HashAlgorithm, string>);
        } catch (error) {
            console.error('Hash generation failed:', error);
        }
    };

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            alert('Copied to clipboard!');
        } catch (e) {
            alert('Failed to copy');
        }
    };

    return (
        <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold mb-2">Hash Generator</h1>
            <p className="text-gray-600 mb-6">Generate cryptographic hashes (SHA-1, SHA-256, SHA-512)</p>

            <div className="tool-card mb-6">
                <label className="font-medium block mb-2">Input Text</label>
                <textarea
                    value={input}
                    onChange={(e) => {
                        setInput(e.target.value);
                        generateHashes();
                    }}
                    onKeyUp={generateHashes}
                    placeholder="Enter text to hash..."
                    className="w-full h-32 p-4 border border-gray-300 rounded-lg font-mono text-sm"
                />
            </div>

            <div className="space-y-4">
                {Object.entries(hashes).map(([algorithm, hash]) => (
                    <div key={algorithm} className="tool-card">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="font-semibold text-lg">{algorithm}</h3>
                            {hash && !hash.includes('not available') && (
                                <button
                                    onClick={() => copyToClipboard(hash)}
                                    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                                >
                                    Copy
                                </button>
                            )}
                        </div>
                        {hash ? (
                            <code className={`block p-3 rounded bg-gray-50 border border-gray-200 text-sm break-all ${hash.includes('not available') ? 'text-gray-500 italic' : 'font-mono'
                                }`}>
                                {hash}
                            </code>
                        ) : (
                            <p className="text-gray-400 italic">Enter text to generate hash</p>
                        )}
                    </div>
                ))}
            </div>

            <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="font-semibold mb-2">⚠️ Security Notice</h3>
                <p className="text-sm text-gray-700">
                    SHA-1 is deprecated for security purposes. Use SHA-256 or SHA-512 for cryptographic applications.
                    MD5 is not available in modern browsers due to security vulnerabilities.
                </p>
            </div>
        </div>
    );
}
